import dashjs, { MediaPlayerClass, MediaPlayerSettingClass, PlaybackErrorEvent } from "dashjs";
import { twMerge } from "tailwind-merge";
import { useEffect, useRef, useState } from "react";
import { MessageResponse, useZoneCamerasQuery } from "../api/hitl-api";
import { useParams } from "react-router-dom";
import { Canvas } from "./canvas";
import { useRect } from "@reach/rect";
import { streamsApiInstance } from "../api/axios/api-instances";

type VideoStreamProps = {
  id?: string;
  hideLabel?: boolean;
  hideVideo?: boolean;
  className?: string;
  videoClassName?: string;
  messageContent: MessageResponse["result"];
};

const getLiveStreamMpd = (cameraId: string | null) =>
  `${streamsApiInstance.defaults.baseURL}/stream/v1/camera/live/${cameraId}`;

const settings: MediaPlayerSettingClass = {
  errors: {
    recoverAttempts: {
      mediaErrorDecode: Infinity,
    },
  },
  streaming: {
    retryAttempts: {
      MPD: Infinity,
    },
    retryIntervals: {
      MPD: 1000,
      MediaSegment: 1000,
      FragmentInfoSegment: 1000,
      InitializationSegment: 1000,
      other: 1000,
      IndexSegment: 1000,
    },
    liveCatchup: {
      maxDrift: 5,
      enabled: true,
    },
    gaps: {
      jumpGaps: true,
      jumpLargeGaps: true,
      enableSeekFix: true,
    },
    timeShiftBuffer: {
      calcFromSegmentTimeline: true,
      fallbackToSegmentTimeline: true,
    },
    buffer: {
      stableBufferTime: 1000,
    },
  },
};

const Video = ({ id, videoClassName }: Omit<VideoStreamProps, "messageContent">) => {
  const dashPlayer = useRef<MediaPlayerClass | null>(null);
  const onError = (e: dashjs.ErrorEvent) => {
    console.log("Error", e);
  };

  const onPlaybackError = (err: PlaybackErrorEvent) => {
    console.log("PlaybackError", err);
  };

  const onPlaybackTimeUpdated = () => {
    if (dashPlayer.current && import.meta.env.DEV) {
      console.debug("LATENCY" + `[${id}]: `, dashPlayer.current.getCurrentLiveLatency());
    }
  };

  const { zoneId = "" } = useParams();
  const { data: cameraId } = useZoneCamerasQuery(undefined, {
    selectFromResult: (data) => {
      const cameraId = data.data?.results.find((camera) => camera.zone_id === zoneId)?.camera_id as
        | string
        | undefined;

      return { data: cameraId };
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaPlayer] = useState(() => dashjs.MediaPlayer().create());

  const onWaiting = () => {
    setIsLoading(true);
  };

  const onCanPlay = () => {
    setIsLoading(false);
  };

  const onPlaying = () => {
    setIsLoading(false);
  };

  useEffect(() => {
    const videoEl = videoRef.current;

    mediaPlayer.initialize(videoRef.current!);
    mediaPlayer.updateSettings(settings);
    mediaPlayer.setAutoPlay(true);

    mediaPlayer.on("error", onError);
    mediaPlayer.on("playbackError", onPlaybackError);
    mediaPlayer.on("playbackTimeUpdated", onPlaybackTimeUpdated);
    videoEl?.addEventListener("waiting", onWaiting);
    videoEl?.addEventListener("canplay", onCanPlay);
    videoEl?.addEventListener("playing", onPlaying);

    return () => {
      mediaPlayer.off("error", onError);
      mediaPlayer.off("playbackError", onPlaybackError);
      mediaPlayer.off("playbackTimeUpdated", onPlaybackTimeUpdated);
      videoEl?.removeEventListener("waiting", onWaiting);
      videoEl?.removeEventListener("canplay", onCanPlay);
      videoEl?.removeEventListener("playing", onPlaying);
    };
  }, []);

  useEffect(() => {
    if (!cameraId) return;

    mediaPlayer.attachSource(getLiveStreamMpd(cameraId));
  }, [cameraId]);

  return (
    <div id={id} className="relative w-full h-full aspect-video bg-black">
      <video ref={videoRef} muted preload="metadata" className={twMerge(["w-full h-full", videoClassName])} />

      <div
        className={twMerge([
          "opacity-0 transition-opacity duration-300 absolute z-2 inset-0 top-0 left-0 flex justify-center pointer-events-none items-center bg-black/20 w-full h-full",
          isLoading && "opacity-100",
        ])}
      >
        <progress></progress>
      </div>
    </div>
  );
};

const VIDEO_ASPECT_RATIO = 16 / 9;

export function SmallVideoStream({
  hideVideo,
  id = "video-stream",
  className,
  videoClassName,
  messageContent,
}: Omit<VideoStreamProps, "player">) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  useRect(wrapperRef, {
    onChange(rect) {
      setStageSize({
        width: rect.height * VIDEO_ASPECT_RATIO,
        height: rect.height,
      });
    },
  });

  return (
    <div className={className} ref={wrapperRef}>
      <div className="relative w-full h-full flex justify-center overflow-hidden rounded-lg">
        {messageContent && messageContent.polygons && (
          <Canvas
            stageSize={stageSize}
            polygons={messageContent?.polygons}
            imageWidth={messageContent?.image_width}
            imageHeight={messageContent?.image_height}
          />
        )}

        {!hideVideo ? (
          <Video id={id} videoClassName={videoClassName} />
        ) : (
          <div className="w-full h-full aspect-video bg-gray-200 rounded-lg" />
        )}
      </div>
    </div>
  );
}
